import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './DNSConfig.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * DNSConfig - Default DNS Configuration Management
 * Complete CRUD operations for managing DNS configurations
 */
function DNSConfig() {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [activeTab, setActiveTab] = useState('basic');
    const [submitting, setSubmitting] = useState(false);

    // Form data structure matching DefaultDNSConfig schema
    const [formData, setFormData] = useState({
        configName: 'default',
        rootDomain: 'ecomtechbd.com',
        isActive: true,

        // IP Pool
        ipPool: [],

        // Bangladesh IP Pool
        bangladeshIpPool: [],

        // Last-resort fallback IP (served only when all pool IPs are down)
        fallbackIp: '',

        // Reserved Subdomains
        reservedSubdomains: [],

        // Name Servers
        nameservers: [],

        // TTL Configuration
        ttl: {
            default: 300,
            a: 300,
            aaaa: 300,
            ns: 3600,
            soa: 3600,
            caa: 3600,
            mx: 3600,
            txt: 300,
            cname: 300,
            ptr: 3600,
            srv: 3600,
            dnskey: 86400
        },

        // SOA Configuration
        soa: {
            refresh: 3600,
            retry: 600,
            expire: 604800,
            minimum: 86400
        },

        // MX Configuration
        mx: {
            priority: 10,
            mailServer: 'mail'
        },

        // SRV Configuration
        srv: {
            priority: 10,
            weight: 60,
            port: 80
        },

        // DNSKEY Configuration
        dnskey: {
            flags: 257,
            protocol: 3,
            algorithm: 8,
            publicKey: ''
        },

        // AAAA Configuration
        aaaa: {
            defaultIpv6: '2001:0db8:85a3:0000:0000:8a2e:0370:7334'
        },

        // CAA Configuration
        caa: {
            issuer: 'letsencrypt.org'
        },

        // Rate Limiting
        rateLimit: {
            requestsPerSecond: 20,
            banTimeMs: 60000
        }
    });

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalConfigs: 0,
        limit: 10
    });

    const [filters, setFilters] = useState({
        isActive: 'all',
        configName: ''
    });

    // Fetch DNS Configurations
    useEffect(() => {
        fetchConfigs();
    }, [pagination.currentPage, filters]);

    const fetchConfigs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');

            const queryParams = new URLSearchParams({
                page: pagination.currentPage,
                limit: pagination.limit,
                ...(filters.isActive !== 'all' && { isActive: filters.isActive }),
                ...(filters.configName && { configName: filters.configName })
            });

            const response = await axios.get(
                `${BASEURL}superadmin/dns-configs/${userId}?${queryParams}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setConfigs(response.data.data.configs);
                setPagination(response.data.data.pagination);
                setError('');
            } else {
                setError(response.data.error || 'Failed to fetch DNS configurations');
            }
        } catch (err) {
            console.error('Error fetching DNS configs:', err);
            setError(err.response?.data?.error || 'Error loading DNS configurations');
        } finally {
            setLoading(false);
        }
    };

    // Delete DNS Configuration
    const handleDelete = async (configId) => {
        if (!window.confirm('Are you sure you want to delete this DNS configuration?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');

            await axios.delete(
                `${BASEURL}superadmin/dns-config/${userId}/${configId}`,
                { headers: { token } }
            );

            alert('DNS configuration deleted successfully!');
            fetchConfigs();
        } catch (err) {
            console.error('Error deleting DNS config:', err);
            alert(err.response?.data?.error || 'Failed to delete DNS configuration');
        }
    };

    // Open modal for create/edit
    const openModal = (config = null) => {
        if (config) {
            setEditingConfig(config);
            setFormData(config);
        } else {
            setEditingConfig(null);
            resetFormData();
        }
        setShowModal(true);
        setActiveTab('basic');
    };

    const resetFormData = () => {
        setFormData({
            configName: 'default',
            rootDomain: 'ecomtechbd.com',
            isActive: true,
            ipPool: [],
            bangladeshIpPool: [],
            fallbackIp: '',
            reservedSubdomains: [],
            nameservers: [],
            ttl: {
                default: 300, a: 300, aaaa: 300, ns: 3600, soa: 3600,
                caa: 3600, mx: 3600, txt: 300, cname: 300, ptr: 3600,
                srv: 3600, dnskey: 86400
            },
            soa: { refresh: 3600, retry: 600, expire: 604800, minimum: 86400 },
            mx: { priority: 10, mailServer: 'mail' },
            srv: { priority: 10, weight: 60, port: 80 },
            dnskey: { flags: 257, protocol: 3, algorithm: 8, publicKey: '' },
            aaaa: { defaultIpv6: '2001:0db8:85a3:0000:0000:8a2e:0370:7334' },
            caa: { issuer: 'letsencrypt.org' },
            rateLimit: { requestsPerSecond: 20, banTimeMs: 60000 }
        });
    };

    // Submit form (Create or Update)
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');

            if (editingConfig) {
                // Update existing config
                await axios.put(
                    `${BASEURL}superadmin/dns-config/${userId}/${editingConfig._id}`,
                    formData,
                    { headers: { token } }
                );
                alert('DNS configuration updated successfully!');
            } else {
                // Create new config
                await axios.post(
                    `${BASEURL}superadmin/dns-config/${userId}`,
                    formData,
                    { headers: { token } }
                );
                alert('DNS configuration created successfully!');
            }

            setShowModal(false);
            fetchConfigs();
        } catch (err) {
            console.error('Error saving DNS config:', err);
            alert(err.response?.data?.error || 'Failed to save DNS configuration');
        } finally {
            setSubmitting(false);
        }
    };

    // Helper to update nested fields
    const updateNestedField = (path, value) => {
        const keys = path.split('.');
        setFormData(prev => {
            const newData = { ...prev };
            let current = newData;

            for (let i = 0; i < keys.length - 1; i++) {
                current[keys[i]] = { ...current[keys[i]] };
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return newData;
        });
    };

    // IP Pool Management
    const addIpToPool = () => {
        setFormData(prev => ({
            ...prev,
            ipPool: [...prev.ipPool, { ip: '', ratio: 1, isActive: true, label: '' }]
        }));
    };

    const removeIpFromPool = (index) => {
        setFormData(prev => ({
            ...prev,
            ipPool: prev.ipPool.filter((_, i) => i !== index)
        }));
    };

    const updateIpPool = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            ipPool: prev.ipPool.map((ip, i) =>
                i === index ? { ...ip, [field]: value } : ip
            )
        }));
    };

    // Bangladesh IP Pool Management
    const addBdIpToPool = () => {
        setFormData(prev => ({
            ...prev,
            bangladeshIpPool: [...prev.bangladeshIpPool, { ip: '', ratio: 1, isActive: true, label: '' }]
        }));
    };

    const removeBdIpFromPool = (index) => {
        setFormData(prev => ({
            ...prev,
            bangladeshIpPool: prev.bangladeshIpPool.filter((_, i) => i !== index)
        }));
    };

    const updateBdIpPool = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            bangladeshIpPool: prev.bangladeshIpPool.map((ip, i) =>
                i === index ? { ...ip, [field]: value } : ip
            )
        }));
    };

    // Reserved Subdomain Management
    const addReservedSubdomain = () => {
        setFormData(prev => ({
            ...prev,
            reservedSubdomains: [...prev.reservedSubdomains, { subdomain: '', description: '', isActive: true }]
        }));
    };

    const removeReservedSubdomain = (index) => {
        setFormData(prev => ({
            ...prev,
            reservedSubdomains: prev.reservedSubdomains.filter((_, i) => i !== index)
        }));
    };

    const updateReservedSubdomain = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            reservedSubdomains: prev.reservedSubdomains.map((sub, i) =>
                i === index ? { ...sub, [field]: value } : sub
            )
        }));
    };

    // Nameserver Management
    const addNameserver = () => {
        setFormData(prev => ({
            ...prev,
            nameservers: [...prev.nameservers, { hostname: '', ip: '', priority: 1, isActive: true }]
        }));
    };

    const removeNameserver = (index) => {
        setFormData(prev => ({
            ...prev,
            nameservers: prev.nameservers.filter((_, i) => i !== index)
        }));
    };

    const updateNameserver = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            nameservers: prev.nameservers.map((ns, i) =>
                i === index ? { ...ns, [field]: value } : ns
            )
        }));
    };

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div>
                        <h1>DNS Configuration Management</h1>
                        <p>Manage default DNS configurations for the platform</p>
                    </div>
                    <button
                        className={styles.addButton}
                        onClick={() => openModal()}
                    >
                        + Add DNS Config
                    </button>
                </div>

                {/* Filters */}
                <div className={styles.filters}>
                    <input
                        type="text"
                        placeholder="Search by config name..."
                        value={filters.configName}
                        onChange={(e) => setFilters({ ...filters, configName: e.target.value })}
                        className={styles.searchInput}
                    />
                    <select
                        value={filters.isActive}
                        onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
                        className={styles.filterSelect}
                    >
                        <option value="all">All Status</option>
                        <option value="true">Active Only</option>
                        <option value="false">Inactive Only</option>
                    </select>
                </div>

                {error && <div className={styles.error}>{error}</div>}

                {loading ? (
                    <div className={styles.loading}>Loading DNS configurations...</div>
                ) : configs.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No DNS configurations found</p>
                        <button onClick={() => openModal()}>Create First Config</button>
                    </div>
                ) : (
                    <>
                        <div className={styles.configGrid}>
                            {configs.map((config) => (
                                <div key={config._id} className={styles.configCard}>
                                    <div className={styles.configHeader}>
                                        <h3>{config.configName}</h3>
                                        <span className={config.isActive ? styles.activeBadge : styles.inactiveBadge}>
                                            {config.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>

                                    <div className={styles.configDetails}>
                                        <div className={styles.detailRow}>
                                            <strong>Root Domain:</strong>
                                            <span>{config.rootDomain}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <strong>IP Pool:</strong>
                                            <span>{config.ipPool?.length || 0} IPs</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <strong>BD IP Pool:</strong>
                                            <span>{config.bangladeshIpPool?.length || 0} IPs</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <strong>Nameservers:</strong>
                                            <span>{config.nameservers?.length || 0} servers</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <strong>Reserved Subdomains:</strong>
                                            <span>{config.reservedSubdomains?.length || 0} domains</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <strong>Rate Limit:</strong>
                                            <span>{config.rateLimit?.requestsPerSecond || 20} req/s</span>
                                        </div>
                                    </div>

                                    <div className={styles.configActions}>
                                        <button
                                            className={styles.editButton}
                                            onClick={() => openModal(config)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className={styles.deleteButton}
                                            onClick={() => handleDelete(config._id)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    disabled={!pagination.hasPrevPage}
                                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                                >
                                    Previous
                                </button>
                                <span>
                                    Page {pagination.currentPage} of {pagination.totalPages}
                                </span>
                                <button
                                    disabled={!pagination.hasNextPage}
                                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Modal for Create/Edit */}
                {showModal && (
                    <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
                        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <h2>{editingConfig ? 'Edit DNS Configuration' : 'Add DNS Configuration'}</h2>
                                <button
                                    className={styles.closeButton}
                                    onClick={() => setShowModal(false)}
                                >
                                    ×
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className={styles.tabs}>
                                <button
                                    className={activeTab === 'basic' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('basic')}
                                >
                                    Basic Info
                                </button>
                                <button
                                    className={activeTab === 'ipPool' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('ipPool')}
                                >
                                    IP Pool
                                </button>
                                <button
                                    className={activeTab === 'bdIpPool' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('bdIpPool')}
                                >
                                    BD IP Pool
                                </button>
                                <button
                                    className={activeTab === 'nameservers' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('nameservers')}
                                >
                                    Nameservers
                                </button>
                                <button
                                    className={activeTab === 'subdomains' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('subdomains')}
                                >
                                    Reserved Subdomains
                                </button>
                                <button
                                    className={activeTab === 'ttl' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('ttl')}
                                >
                                    TTL & DNS
                                </button>
                                <button
                                    className={activeTab === 'advanced' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('advanced')}
                                >
                                    Advanced
                                </button>
                            </div>

                            {/* Tab Content wrapped in form */}
                            <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                                {/* Basic Info Tab */}
                                {activeTab === 'basic' && (
                                    <div className={styles.tabContent}>
                                        <div className={styles.formGroup}>
                                            <label>Config Name*</label>
                                            <input
                                                type="text"
                                                value={formData.configName}
                                                onChange={(e) => setFormData({ ...formData, configName: e.target.value })}
                                                placeholder="e.g., default, production"
                                                required
                                            />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Root Domain*</label>
                                            <input
                                                type="text"
                                                value={formData.rootDomain}
                                                onChange={(e) => setFormData({ ...formData, rootDomain: e.target.value })}
                                                placeholder="e.g., ecomtechbd.com"
                                                required
                                            />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.isActive}
                                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                                />
                                                Active Configuration
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {/* IP Pool Tab */}
                                {activeTab === 'ipPool' && (
                                    <div className={styles.tabContent}>
                                        <div className={styles.arrayHeader}>
                                            <h3>IP Pool ({formData.ipPool.length})</h3>
                                            <button type="button" onClick={addIpToPool} className={styles.addArrayButton}>
                                                + Add IP
                                            </button>
                                        </div>

                                        {formData.ipPool.map((ip, index) => (
                                            <div key={index} className={styles.arrayItem}>
                                                <div className={styles.formRow}>
                                                    <div className={styles.formGroup}>
                                                        <label>IP Address*</label>
                                                        <input
                                                            type="text"
                                                            value={ip.ip}
                                                            onChange={(e) => updateIpPool(index, 'ip', e.target.value)}
                                                            placeholder="192.168.1.1"
                                                            required
                                                        />
                                                    </div>
                                                    <div className={styles.formGroup}>
                                                        <label>Ratio (0-100)</label>
                                                        <input
                                                            type="number"
                                                            value={ip.ratio}
                                                            onChange={(e) => updateIpPool(index, 'ratio', parseInt(e.target.value) || 1)}
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                                <div className={styles.formRow}>
                                                    <div className={styles.formGroup}>
                                                        <label>Label</label>
                                                        <input
                                                            type="text"
                                                            value={ip.label}
                                                            onChange={(e) => updateIpPool(index, 'label', e.target.value)}
                                                            placeholder="e.g., Primary Server"
                                                        />
                                                    </div>
                                                    <div className={styles.formGroup}>
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                checked={ip.isActive}
                                                                onChange={(e) => updateIpPool(index, 'isActive', e.target.checked)}
                                                            />
                                                            Active
                                                        </label>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeIpFromPool(index)}
                                                    className={styles.removeArrayButton}
                                                >
                                                    Remove IP
                                                </button>
                                            </div>
                                        ))}

                                        {formData.ipPool.length === 0 && (
                                            <p className={styles.emptyArray}>No IPs in pool. Click "Add IP" to add one.</p>
                                        )}
                                    </div>
                                )}

                                {/* Bangladesh IP Pool Tab */}
                                {activeTab === 'bdIpPool' && (
                                    <div className={styles.tabContent}>
                                        <div className={styles.arrayHeader}>
                                            <h3>Bangladesh IP Pool ({formData.bangladeshIpPool?.length || 0})</h3>
                                            <button type="button" onClick={addBdIpToPool} className={styles.addArrayButton}>
                                                + Add BD IP
                                            </button>
                                        </div>

                                        {(formData.bangladeshIpPool || []).map((ip, index) => (
                                            <div key={index} className={styles.arrayItem}>
                                                <div className={styles.formRow}>
                                                    <div className={styles.formGroup}>
                                                        <label>IP Address*</label>
                                                        <input
                                                            type="text"
                                                            value={ip.ip}
                                                            onChange={(e) => updateBdIpPool(index, 'ip', e.target.value)}
                                                            placeholder="103.76.44.21"
                                                            required
                                                        />
                                                    </div>
                                                    <div className={styles.formGroup}>
                                                        <label>Ratio (0-100)</label>
                                                        <input
                                                            type="number"
                                                            value={ip.ratio}
                                                            onChange={(e) => updateBdIpPool(index, 'ratio', parseInt(e.target.value) || 1)}
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                                <div className={styles.formRow}>
                                                    <div className={styles.formGroup}>
                                                        <label>Label</label>
                                                        <input
                                                            type="text"
                                                            value={ip.label}
                                                            onChange={(e) => updateBdIpPool(index, 'label', e.target.value)}
                                                            placeholder="e.g., BD ISP-1"
                                                        />
                                                    </div>
                                                    <div className={styles.formGroup}>
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                checked={ip.isActive}
                                                                onChange={(e) => updateBdIpPool(index, 'isActive', e.target.checked)}
                                                            />
                                                            Active
                                                        </label>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeBdIpFromPool(index)}
                                                    className={styles.removeArrayButton}
                                                >
                                                    Remove BD IP
                                                </button>
                                            </div>
                                        ))}

                                {(!formData.bangladeshIpPool || formData.bangladeshIpPool.length === 0) && (
                                            <p className={styles.emptyArray}>No Bangladesh IPs in pool. Click "Add BD IP" to add one.</p>
                                        )}

                                        <div className={styles.arrayItem} style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '14px' }}>
                                            <div className={styles.formGroup}>
                                                <label>Fallback IP (last resort)</label>
                                                <input
                                                    type="text"
                                                    value={formData.fallbackIp || ''}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, fallbackIp: e.target.value }))}
                                                    placeholder="e.g., 57.128.197.50 (Poland)"
                                                />
                                                <small style={{ color: '#6b7280' }}>
                                                    Served only when <strong>all</strong> pool IPs are down (bypasses its health check).
                                                    Auto-reverts to the pool as soon as any IP is healthy again. Leave empty to disable.
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Nameservers Tab */}
                                {activeTab === 'nameservers' && (
                                    <div className={styles.tabContent}>
                                        <div className={styles.arrayHeader}>
                                            <h3>Nameservers ({formData.nameservers.length})</h3>
                                            <button type="button" onClick={addNameserver} className={styles.addArrayButton}>
                                                + Add Nameserver
                                            </button>
                                        </div>

                                        {formData.nameservers.map((ns, index) => (
                                            <div key={index} className={styles.arrayItem}>
                                                <div className={styles.formRow}>
                                                    <div className={styles.formGroup}>
                                                        <label>Hostname*</label>
                                                        <input
                                                            type="text"
                                                            value={ns.hostname}
                                                            onChange={(e) => updateNameserver(index, 'hostname', e.target.value)}
                                                            placeholder="ns1.example.com"
                                                            required
                                                        />
                                                    </div>
                                                    <div className={styles.formGroup}>
                                                        <label>IP Address*</label>
                                                        <input
                                                            type="text"
                                                            value={ns.ip}
                                                            onChange={(e) => updateNameserver(index, 'ip', e.target.value)}
                                                            placeholder="192.168.1.1"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className={styles.formRow}>
                                                    <div className={styles.formGroup}>
                                                        <label>Priority</label>
                                                        <input
                                                            type="number"
                                                            value={ns.priority}
                                                            onChange={(e) => updateNameserver(index, 'priority', parseInt(e.target.value) || 1)}
                                                            min="1"
                                                        />
                                                    </div>
                                                    <div className={styles.formGroup}>
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                checked={ns.isActive}
                                                                onChange={(e) => updateNameserver(index, 'isActive', e.target.checked)}
                                                            />
                                                            Active
                                                        </label>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeNameserver(index)}
                                                    className={styles.removeArrayButton}
                                                >
                                                    Remove Nameserver
                                                </button>
                                            </div>
                                        ))}

                                        {formData.nameservers.length === 0 && (
                                            <p className={styles.emptyArray}>No nameservers configured. Click "Add Nameserver" to add one.</p>
                                        )}
                                    </div>
                                )}

                                {/* Reserved Subdomains Tab */}
                                {activeTab === 'subdomains' && (
                                    <div className={styles.tabContent}>
                                        <div className={styles.arrayHeader}>
                                            <h3>Reserved Subdomains ({formData.reservedSubdomains.length})</h3>
                                            <button type="button" onClick={addReservedSubdomain} className={styles.addArrayButton}>
                                                + Add Subdomain
                                            </button>
                                        </div>

                                        {formData.reservedSubdomains.map((sub, index) => (
                                            <div key={index} className={styles.arrayItem}>
                                                <div className={styles.formRow}>
                                                    <div className={styles.formGroup}>
                                                        <label>Subdomain*</label>
                                                        <input
                                                            type="text"
                                                            value={sub.subdomain}
                                                            onChange={(e) => updateReservedSubdomain(index, 'subdomain', e.target.value)}
                                                            placeholder="www, api, admin"
                                                            required
                                                        />
                                                    </div>
                                                    <div className={styles.formGroup}>
                                                        <label>Description</label>
                                                        <input
                                                            type="text"
                                                            value={sub.description}
                                                            onChange={(e) => updateReservedSubdomain(index, 'description', e.target.value)}
                                                            placeholder="Purpose of this subdomain"
                                                        />
                                                    </div>
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={sub.isActive}
                                                            onChange={(e) => updateReservedSubdomain(index, 'isActive', e.target.checked)}
                                                        />
                                                        Active
                                                    </label>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeReservedSubdomain(index)}
                                                    className={styles.removeArrayButton}
                                                >
                                                    Remove Subdomain
                                                </button>
                                            </div>
                                        ))}

                                        {formData.reservedSubdomains.length === 0 && (
                                            <p className={styles.emptyArray}>No reserved subdomains. Click "Add Subdomain" to add one.</p>
                                        )}
                                    </div>
                                )}

                                {/* TTL & DNS Tab */}
                                {activeTab === 'ttl' && (
                                    <div className={styles.tabContent}>
                                        <h3>TTL Configuration (seconds)</h3>
                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>Default TTL</label>
                                                <input
                                                    type="number"
                                                    value={formData.ttl.default}
                                                    onChange={(e) => updateNestedField('ttl.default', parseInt(e.target.value) || 300)}
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>A Record</label>
                                                <input
                                                    type="number"
                                                    value={formData.ttl.a}
                                                    onChange={(e) => updateNestedField('ttl.a', parseInt(e.target.value) || 300)}
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>AAAA Record</label>
                                                <input
                                                    type="number"
                                                    value={formData.ttl.aaaa}
                                                    onChange={(e) => updateNestedField('ttl.aaaa', parseInt(e.target.value) || 300)}
                                                />
                                            </div>
                                        </div>

                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>NS Record</label>
                                                <input
                                                    type="number"
                                                    value={formData.ttl.ns}
                                                    onChange={(e) => updateNestedField('ttl.ns', parseInt(e.target.value) || 3600)}
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>SOA Record</label>
                                                <input
                                                    type="number"
                                                    value={formData.ttl.soa}
                                                    onChange={(e) => updateNestedField('ttl.soa', parseInt(e.target.value) || 3600)}
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>MX Record</label>
                                                <input
                                                    type="number"
                                                    value={formData.ttl.mx}
                                                    onChange={(e) => updateNestedField('ttl.mx', parseInt(e.target.value) || 3600)}
                                                />
                                            </div>
                                        </div>

                                        <h3 style={{ marginTop: '20px' }}>SOA Configuration</h3>
                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>Refresh</label>
                                                <input
                                                    type="number"
                                                    value={formData.soa.refresh}
                                                    onChange={(e) => updateNestedField('soa.refresh', parseInt(e.target.value) || 3600)}
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>Retry</label>
                                                <input
                                                    type="number"
                                                    value={formData.soa.retry}
                                                    onChange={(e) => updateNestedField('soa.retry', parseInt(e.target.value) || 600)}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>Expire</label>
                                                <input
                                                    type="number"
                                                    value={formData.soa.expire}
                                                    onChange={(e) => updateNestedField('soa.expire', parseInt(e.target.value) || 604800)}
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>Minimum</label>
                                                <input
                                                    type="number"
                                                    value={formData.soa.minimum}
                                                    onChange={(e) => updateNestedField('soa.minimum', parseInt(e.target.value) || 86400)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Advanced Tab */}
                                {activeTab === 'advanced' && (
                                    <div className={styles.tabContent}>
                                        <h3>MX Configuration</h3>
                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>Priority</label>
                                                <input
                                                    type="number"
                                                    value={formData.mx.priority}
                                                    onChange={(e) => updateNestedField('mx.priority', parseInt(e.target.value) || 10)}
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>Mail Server</label>
                                                <input
                                                    type="text"
                                                    value={formData.mx.mailServer}
                                                    onChange={(e) => updateNestedField('mx.mailServer', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <h3 style={{ marginTop: '20px' }}>CAA Configuration</h3>
                                        <div className={styles.formGroup}>
                                            <label>Issuer</label>
                                            <input
                                                type="text"
                                                value={formData.caa.issuer}
                                                onChange={(e) => updateNestedField('caa.issuer', e.target.value)}
                                                placeholder="letsencrypt.org"
                                            />
                                        </div>

                                        <h3 style={{ marginTop: '20px' }}>IPv6 (AAAA) Configuration</h3>
                                        <div className={styles.formGroup}>
                                            <label>Default IPv6</label>
                                            <input
                                                type="text"
                                                value={formData.aaaa.defaultIpv6}
                                                onChange={(e) => updateNestedField('aaaa.defaultIpv6', e.target.value)}
                                                placeholder="2001:0db8:85a3:0000:0000:8a2e:0370:7334"
                                            />
                                        </div>

                                        <h3 style={{ marginTop: '20px' }}>Rate Limiting</h3>
                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>Requests Per Second</label>
                                                <input
                                                    type="number"
                                                    value={formData.rateLimit.requestsPerSecond}
                                                    onChange={(e) => updateNestedField('rateLimit.requestsPerSecond', parseInt(e.target.value) || 20)}
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>Ban Time (ms)</label>
                                                <input
                                                    type="number"
                                                    value={formData.rateLimit.banTimeMs}
                                                    onChange={(e) => updateNestedField('rateLimit.banTimeMs', parseInt(e.target.value) || 60000)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </form>

                            {/* Modal Footer - Outside form for fixed positioning */}
                            <div className={styles.modalFooter}>
                                <button
                                    type="button"
                                    className={styles.cancelButton}
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={styles.submitButton}
                                    disabled={submitting}
                                    onClick={handleSubmit}
                                >
                                    {submitting ? 'Saving...' : editingConfig ? 'Update Configuration' : 'Create Configuration'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default DNSConfig;
